/** Frames in a row this quick (ms) before the game counts as steady. */
const STEADY_FRAME_MS = 34;
const STEADY_FRAMES = 20;
/** Shown at least this long after joining (ms), so it doesn't just flicker... */
const MIN_MS = 600;
/** ...and never longer than this: a slow machine that never runs steady still gets in. */
const MAX_MS = 10_000;
/** How long the fade out takes (ms); matches #loading's transition in style.css. */
const FADE_MS = 400;

/**
 * The loading screen, from Launch until the game has settled: we've joined,
 * the server has told us where we are, the rocks round us are built, and the
 * frame rate has calmed down (the first frames after joining paint every rock
 * and sprite for the first time). Then it fades and the game is under it.
 */
export class LoadingScreen {
  private readonly root = document.getElementById("loading")!;
  private readonly fill = this.root.querySelector<HTMLElement>(".loading-fill")!;
  private readonly text = this.root.querySelector<HTMLElement>(".loading-text")!;

  private active = false;
  /** When we joined (performance.now), or null while still connecting. */
  private joinedAt: number | null = null;
  private snapshot = false;
  private steady = 0;
  /** The bar as drawn, eased towards where it should be. */
  private shown = 0;
  private fadeTimer: ReturnType<typeof setTimeout> | undefined;

  get isOpen(): boolean {
    return this.active;
  }

  /** Start over: connecting. */
  show(): void {
    this.active = true;
    this.joinedAt = null;
    this.snapshot = false;
    this.steady = 0;
    this.shown = 0;
    clearTimeout(this.fadeTimer);
    this.root.classList.remove("hidden", "fading");
  }

  /** Joined (or rejoined, with the world built afresh): now wait for it to settle. */
  joined(): void {
    if (!this.active) this.show();
    this.joinedAt = performance.now();
    this.snapshot = false;
    this.steady = 0;
  }

  /** The server has said where we are and what's round us. */
  located(): void {
    if (this.joinedAt !== null) this.snapshot = true;
  }

  /**
   * Once a frame. `frameMs`: how long the last one took; `status`: what the
   * connection is doing (before we've joined); `building`: rocks near us are
   * still waiting to be built.
   */
  update(frameMs: number, status: string, building: boolean): void {
    if (!this.active) return;
    const now = performance.now();
    this.steady = frameMs < STEADY_FRAME_MS ? this.steady + 1 : 0;

    let target: number;
    let text: string;
    if (this.joinedAt === null) {
      target = /join/i.test(status) ? 0.25 : 0.1;
      text = status || "Connecting...";
    } else {
      const steady = Math.min(1, this.steady / STEADY_FRAMES);
      target = 0.4 + (this.snapshot ? 0.2 : 0) + (this.snapshot && !building ? 0.2 : 0) + (this.snapshot && !building ? 0.2 * steady : 0);
      text = !this.snapshot ? "Finding you..." : building ? "Building the rocks round you..." : "Settling in...";
      const since = now - this.joinedAt;
      const settled = this.snapshot && !building && this.steady >= STEADY_FRAMES && since >= MIN_MS;
      if (settled || since >= MAX_MS) {
        target = 1;
        this.finish();
      }
    }
    this.shown += (target - this.shown) * 0.15;
    if (target === 1) this.shown = 1;
    this.fill.style.width = `${Math.round(this.shown * 100)}%`;
    if (this.text.textContent !== text) this.text.textContent = text;
  }

  /** Gone at once (the join failed: the join screen is back). */
  hide(): void {
    this.active = false;
    clearTimeout(this.fadeTimer);
    this.root.classList.add("hidden");
    this.root.classList.remove("fading");
  }

  private finish(): void {
    this.active = false;
    this.root.classList.add("fading");
    this.fadeTimer = setTimeout(() => this.hide(), FADE_MS);
  }
}
